// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';

// 3rd party imports
import {
  DxProgressBarComponent,
  DxTextBoxComponent,
  DxValidationGroupComponent,
  DxPopupComponent
} from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import {
  CCPasswordHelperService,
  IconConstants,
  MessageBoxComponent,
  Utils,
  RxjsUtils
} from '@therealmanage/ciranet-ui-common';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { UserBackgroundService } from '../../_services/shared-services/user-background.service';

// Model imports
import { PasswordUpdateDto } from '../../_models/password-update-dto';

// Constant imports
import { Constants } from '../../_constants/constants';
import { MessageInterval } from '../../_constants/messages-intervals';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: false
})
export class ChangePasswordComponent implements OnInit {
  @ViewChild('popupChangePassword', { static: false })
  popupChangePassword: DxPopupComponent;
  @ViewChild('validatorChangePassword', { static: false })
  validatorChangePassword: DxValidationGroupComponent;
  @ViewChild('currentPassword', { static: false })
  currentPassword: DxTextBoxComponent;
  @ViewChild('newPassword', { static: false }) newPassword: DxTextBoxComponent;
  @ViewChild('confirmPassword', { static: false })
  confirmPassword: DxTextBoxComponent;
  @ViewChild('passwordStrengthIndicator', { static: false })
  passwordStrengthIndicator: DxProgressBarComponent;
  @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

  title = 'Change Password';
  loadingVisible = false;
  popupVisible = false;
  changePasswordInfo: PasswordUpdateDto = new PasswordUpdateDto();
  passwordStrength = 0;
  passwordSpecialCharactersAllowable =
    Constants.passwordSpecialCharactersAllowable;
  cancelButtonIcon = IconConstants.CANCEL;
  saveButtonIcon = IconConstants.SAVE;
  iconCircleCheck = IconConstants.CIRCLE_CHECK;

  passwordMode: string;
  passwordShowButton: object;
  passwordHideButton: object;
  passwordRules: any[] = [];

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private userBackgroundService: UserBackgroundService,
    private errorService: ServerErrorService,
    private passwordHelper: CCPasswordHelperService
  ) {}

  ngOnInit(): void {
    this.initializePasswordRules();
    this.passwordMode = 'password';
    this.passwordShowButton = {
      icon: 'fas fa-eye',
      type: 'default',
      stylingMode: 'text',
      hint: 'Show Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.passwordHideButton = {
      icon: 'fas fa-eye-slash',
      type: 'default',
      stylingMode: 'text',
      hint: 'Hide Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.changePasswordInfo.userID = this.userService.getCurrentUserId();
    const userInfo = this.userService.getCurrentUserInfo();
    if (userInfo) {
      this.title = `Change Password - ${userInfo.displayName}`;
    }

    // after impersonate
    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectImpersonateChanged(),
      () => {
        this.changePasswordInfo.userID = this.userService.getCurrentUserId();
        const userInfo = this.userService.getCurrentUserInfo();
        if (userInfo) {
          this.title = `Change Password - ${userInfo.displayName}`;
        }
      }
    );
  }

  show(): void {
    this.popupVisible = true;
  }

  hide(): void {
    this.popupVisible = false;
  }

  repaint(): void {
    this.popupChangePassword.instance.repaint();
  }

  passwordComparison = () => {
    return this.newPassword.value;
  };

  btnSaveClick(): void {
    if (this.validatorChangePassword.instance.validate().isValid == false) {
      return;
    }

    if (this.newPassword.value.length < 8) {
      this.messageBox.showErrorMessage(
        'Error',
        'Password must have at least 8 characters.'
      );
      return;
    }

    if (
      Utils.isFalse(
        this.passwordHelper.hasSpecialCharacter(this.newPassword.value)
      )
    ) {
      this.messageBox.showErrorMessage(
        'Error',
        'Your password must contain at least one special character'
      );
      return;
    }

    if (Utils.isFalse(this.passwordHelper.hasNumeric(this.newPassword.value))) {
      this.messageBox.showErrorMessage(
        'Error',
        'Your password must contain at least one number.'
      );
      return;
    }

    if (this.passwordStrength < 5) {
      this.messageBox.showErrorMessage(
        'Error',
        `Your password must include at least one lowercase letter and one uppercase letter.`
      );
      return;
    }

    this.loadingVisible = true;
    RxjsUtils.safeSubscribe(
      this.authService.updatePassword(this.changePasswordInfo),
      (errorMessage) => {
        this.loadingVisible = false;
        if (errorMessage != '') {
          notify(
            errorMessage,
            'error',
            MessageInterval.defaultErrorMsgInterval
          );
        } else {
          notify(
            `New password has been updated successfully.`,
            'success',
            MessageInterval.defaultErrorMsgInterval
          );
          this.popupVisible = false;

          this.currentPassword.value = '';
          this.newPassword.value = '';
          this.confirmPassword.value = '';
          this.changePasswordInfo.currentPassword = '';
          this.changePasswordInfo.newPassword = '';

          this.userBackgroundService.logActivity(
            `change_password`,
            'ChangePassword',
            new Date(),
            'success'
          );
          this.validatorChangePassword.instance.reset();
        }
      },
      (error) => {
        this.loadingVisible = false;
        this.errorService.displayError(error);
      }
    );
  }

  runPasswordValidation(e: any): void {
    const value = e.event.target.value;
    this.passwordStrength = this.passwordHelper.getPasswordStrength(value);
    this.passwordStrengthIndicator.value = this.passwordStrength;
    this.passwordRules.forEach((rule) => {
      rule.isValid = rule.check(value);
    });
  }

  getPasswordStrength(value: number): string {
    return 'Password Strength ' + value * 100 + '%';
  }

  private initializePasswordRules(): void {
    this.passwordRules = [
      {
        label: '8 characters.',
        isValid: false,
        check: (value: string) => value.length >= 8
      },
      {
        label: 'One lowercase (a-z) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasLowerCase(value)
      },
      {
        label: 'One uppercase (A-Z) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasUpperCase(value)
      },
      {
        label: 'One number (0-9) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasNumeric(value)
      },
      {
        label: `Special characters (${this.passwordSpecialCharactersAllowable}).`,
        isValid: false,
        check: (value: string) => this.passwordHelper.hasSpecialCharacter(value)
      }
    ];
  }
}
